import { Trash2 } from "lucide-react";
import { useState } from "react";
import { browser } from "wxt/browser";
import { Button } from "@/components/ui/atoms/button";
import { Input } from "@/components/ui/atoms/input";
import { normalizeSite, type SiteRule } from "@/lib/agent/policy";
import { setSiteRule, useSiteRules } from "@/lib/agent/policy-store";
import { cn } from "@/lib/utils";

const RULES: { id: SiteRule; name: string; description: string }[] = [
  { id: "allow", name: "Allow", description: "Acts without asking (sensitive actions still ask)" },
  { id: "ask", name: "Ask", description: "Asks before every action" },
  { id: "deny", name: "Deny", description: "Never reads or acts on the site" },
];

/** Whether this page can reach the extension's storage (not the website build of the app). */
function inExtension(): boolean {
  try {
    return !!browser?.storage?.local;
  } catch {
    return false;
  }
}

/**
 * Settings → Agent (ADMIN-220): per-site rules for the browser agent. Deny wins over a parent
 * domain's Allow; sensitive actions (buying, sending, submitting) ask whatever the rule says.
 */
export function AgentSettings() {
  if (!inExtension()) {
    return (
      <div className="flex max-w-2xl flex-col gap-2">
        <h2 className="text-lg font-semibold">Agent</h2>
        <p className="text-muted-foreground text-sm">
          The browser agent runs in the Kourai extension. Open these settings there.
        </p>
      </div>
    );
  }
  return <SiteRules />;
}

function SiteRules() {
  const rules = useSiteRules();
  const [site, setSite] = useState("");
  const [rule, setRule] = useState<SiteRule>("deny");
  const [error, setError] = useState<string | null>(null);
  const entries = Object.entries(rules).sort(([a], [b]) => a.localeCompare(b));

  const add = () => {
    const normalized = normalizeSite(site);
    if (!normalized) {
      setError("Type a site like shop.example.com.");
      return;
    }
    setError(null);
    setSite("");
    void setSiteRule(normalized, rule);
  };

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold">Agent</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          What the agent in the side panel may do on each site. Each chat also has a mode (Read
          only, Ask before acting, Act without asking) next to its model. Buying, paying, sending,
          posting and submitting forms always ask.
        </p>
      </div>

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <Input
          aria-label="Site"
          value={site}
          onChange={(e) => setSite(e.target.value)}
          placeholder="shop.example.com"
          className="h-8 min-w-0 flex-[1_1_12rem] text-xs"
        />
        <fieldset className="bg-muted flex gap-0.5 rounded-lg p-0.5" aria-label="Rule">
          {RULES.map((r) => (
            <button
              key={r.id}
              type="button"
              aria-pressed={rule === r.id}
              title={r.description}
              onClick={() => setRule(r.id)}
              className={cn(
                "h-7 rounded-md px-2.5 text-xs font-medium transition-colors",
                rule === r.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {r.name}
            </button>
          ))}
        </fieldset>
        <Button type="submit" size="sm" disabled={!site.trim()}>
          Add rule
        </Button>
        {error && (
          <p role="alert" className="text-destructive w-full text-xs">
            {error}
          </p>
        )}
      </form>

      {entries.length ? (
        <ul className="divide-y rounded-xl border" aria-label="Site rules">
          {entries.map(([name, r]) => (
            <li key={name} className="flex items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1 truncate text-sm">{name}</span>
              <span className="text-muted-foreground text-xs">
                {RULES.find((x) => x.id === r)?.name}
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove the rule for ${name}`}
                onClick={() => void setSiteRule(name, null)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm">
          No site rules yet. In Ask before acting, the agent asks the first time it acts on a site
          in a chat.
        </p>
      )}
    </div>
  );
}
